import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, ExternalLink, Tag, User } from 'lucide-react';
import Alert from '../UI/Alert';
import LoadingIndicator from '../Common/LoadingIndicator';
import Card from '../UI/Card';
import PeriodoFilterButton from './PeriodoFilterButton';
import TarefaStatusFilter from '../AgendaTarefas/TarefaStatusFilter';
import TarefaProfissionalFilter from '../AgendaTarefas/TarefaProfissionalFilter';
import PrimaryButton from '../Common/PrimaryButton';
import { useHistoricoMedicacoes } from '../../hooks/data/useHistoricoMedicacoes';
import { usePermissions } from '../../context/PermissionContext';

const PacienteMedicacaoTab = ({ pacienteId }) => {
    const navigate = useNavigate();
    const { checkPermission } = usePermissions();
    const [activeSection, setActiveSection] = useState('planos-ativos'); // 'planos-ativos', 'planos-inativos', 'tarefas-executadas'
    const [periodo, setPeriodo] = useState('');
    const [periodoStartDate, setPeriodoStartDate] = useState('');
    const [periodoEndDate, setPeriodoEndDate] = useState('');
    
    // Estados para filtros flutuantes
    const [filtroStatus, setFiltroStatus] = useState([]);
    const [filtroProfissionais, setFiltroProfissionais] = useState([]);
    
    const [showStatusFilter, setShowStatusFilter] = useState(false);
    const statusFilterRef = useRef(null);
    const statusButtonRef = useRef(null);
    
    const [showProfissionalFilter, setShowProfissionalFilter] = useState(false);
    const profissionalFilterRef = useRef(null);
    const profissionalButtonRef = useRef(null);
    
    // Verificar permissão para acessar planos médicos
    const canAccessPlanos = checkPermission('planos_medicos', 'can_access');
    
    const { data, isLoading, error, refetch } = useHistoricoMedicacoes(
        pacienteId, 
        { 
            limit: 100, 
            startDate: periodoStartDate || null, 
            endDate: periodoEndDate || null 
        }
    );

    // Handler para mudanças no período
    const handlePeriodoChange = useCallback((newPeriodo) => {
        setPeriodo(newPeriodo || '');
    }, []);

    const handleDateRangeChange = useCallback((newStartDate, newEndDate) => {
        setPeriodoStartDate(newStartDate || '');
        setPeriodoEndDate(newEndDate || '');
        // Refetch com novos filtros
        setTimeout(() => refetch(), 100);
    }, [refetch]);

    // Fechar dropdowns ao clicar fora
    useEffect(() => {
        if (!showStatusFilter && !showProfissionalFilter) return;

        const handleClickOutside = (event) => {
            if (
                (showStatusFilter && statusFilterRef.current && statusButtonRef.current &&
                    !statusFilterRef.current.contains(event.target) &&
                    !statusButtonRef.current.contains(event.target)) ||
                (showProfissionalFilter && profissionalFilterRef.current && profissionalButtonRef.current &&
                    !profissionalFilterRef.current.contains(event.target) &&
                    !profissionalButtonRef.current.contains(event.target))
            ) {
                setShowStatusFilter(false);
                setShowProfissionalFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showStatusFilter, showProfissionalFilter]);

    // Fechar dropdowns ao pressionar Escape
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowStatusFilter(false);
                setShowProfissionalFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    // Aplicar filtros aos dados exibidos
    const filteredData = useMemo(() => {
        if (!data) return data;
        
        let filtered = { ...data };
        
        // Filtrar por profissional se selecionado
        if (filtroProfissionais && filtroProfissionais.length > 0) {
            const profIds = filtroProfissionais.map(id => id.toString());
            if (filtered.tarefas_executadas) {
                filtered.tarefas_executadas = filtered.tarefas_executadas.filter(t => 
                    t.usuario_id && profIds.includes(t.usuario_id.toString())
                );
            }
        }
        
        // Filtrar por status se selecionado
        if (filtroStatus && filtroStatus.length > 0) {
            if (filtered.tarefas_executadas) {
                filtered.tarefas_executadas = filtered.tarefas_executadas.filter(t => {
                    const statusLower = t.status?.toLowerCase() || '';
                    return filtroStatus.some(s => statusLower.includes(s.toLowerCase()));
                });
            }
        }
        
        return filtered;
    }, [data, filtroStatus, filtroProfissionais]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return '-';
        // Se for apenas hora (HH:mm), retorna como está
        if (timeString.match(/^\d{2}:\d{2}$/)) {
            return timeString;
        }
        // Se for datetime, extrai apenas a hora
        const date = new Date(timeString);
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const formatFrequency = (plano) => {
        if (plano.frequencia_tipo === 'Recorrente') {
            if (plano.frequencia_vezes_ao_dia) {
                return `${plano.frequencia_vezes_ao_dia}x ao dia`;
            }
            if (plano.frequencia_intervalo_horas) {
                return `A cada ${plano.frequencia_intervalo_horas}h`;
            }
            return 'Recorrente';
        }
        return plano.frequencia_tipo || '-';
    };

    const sections = [
        { id: 'planos-ativos', label: 'Planos Ativos', count: filteredData?.planos_ativos?.length || 0 },
        { id: 'planos-inativos', label: 'Histórico de Planos', count: filteredData?.planos_inativos?.length || 0 },
        { id: 'tarefas-executadas', label: 'Tarefas Executadas', count: filteredData?.tarefas_executadas?.length || 0 }
    ];

    return (
        <div className="relative space-y-4">
            {/* Botões Flutuantes à Direita - Filtros */}
            <div className="sticky top-0 self-end z-30 flex flex-row gap-2 mb-4 w-fit ml-auto">
                {/* Botão de Status */}
                <div ref={statusButtonRef} className="relative">
                    <PrimaryButton
                        onClick={() => {
                            setShowStatusFilter(!showStatusFilter);
                            setShowProfissionalFilter(false);
                        }}
                        active={showStatusFilter}
                        variant="primary"
                        size="md"
                        icon={<Tag className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showStatusFilter ? 'scale-110' : ''} transition-transform`} />}
                        title={showStatusFilter ? 'Ocultar filtro de status' : 'Mostrar filtro de status'}
                    >
                        <span className="hidden sm:inline whitespace-nowrap">Status</span>
                    </PrimaryButton>
                    {/* Badge indicando filtro ativo */}
                    {filtroStatus && filtroStatus.length > 0 && (
                        <span className="absolute -top-1 -right-1 h-4 w-4 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                            <span className="text-[8px] text-white font-bold">{filtroStatus.length}</span>
                        </span>
                    )}
                    {/* Filtro de Status Flutuante - Dropdown */}
                    {showStatusFilter && (
                        <div ref={statusFilterRef} className="absolute top-full right-0 mt-2 z-[100] w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[200px] max-h-[calc(100vh-8rem)] overflow-y-auto">
                            <TarefaStatusFilter
                                status={filtroStatus}
                                onStatusChange={(newStatus) => {
                                    setFiltroStatus(newStatus);
                                }}
                                onClose={() => setShowStatusFilter(false)}
                            />
                        </div>
                    )}
                </div>

                {/* Botão de Profissional */}
                <div ref={profissionalButtonRef} className="relative">
                    <PrimaryButton
                        onClick={() => {
                            setShowProfissionalFilter(!showProfissionalFilter);
                            setShowStatusFilter(false);
                        }}
                        active={showProfissionalFilter}
                        variant="primary"
                        size="md"
                        icon={<User className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showProfissionalFilter ? 'scale-110' : ''} transition-transform`} />}
                        title={showProfissionalFilter ? 'Ocultar filtro de profissional' : 'Mostrar filtro de profissional'}
                    >
                        <span className="hidden sm:inline whitespace-nowrap">Profissional</span>
                    </PrimaryButton>
                    {/* Badge indicando filtro ativo */}
                    {filtroProfissionais && filtroProfissionais.length > 0 && (
                        <span className="absolute -top-1 -right-1 h-4 w-4 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                            <span className="text-[8px] text-white font-bold">{filtroProfissionais.length}</span>
                        </span>
                    )}
                    {/* Filtro de Profissional Flutuante - Dropdown */}
                    {showProfissionalFilter && (
                        <div ref={profissionalFilterRef} className="absolute top-full right-0 mt-2 z-[100] w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[220px] max-h-[calc(100vh-8rem)] overflow-y-auto">
                            <TarefaProfissionalFilter
                                profissionais={filtroProfissionais}
                                onProfissionaisChange={(newProfissionais) => {
                                    setFiltroProfissionais(newProfissionais);
                                }}
                                onClose={() => setShowProfissionalFilter(false)}
                            />
                        </div>
                    )}
                </div>

                {/* Botão de Período */}
                <div className="relative">
                    <PeriodoFilterButton
                        periodo={periodo}
                        startDate={periodoStartDate}
                        endDate={periodoEndDate}
                        onPeriodoChange={handlePeriodoChange}
                        onDateRangeChange={handleDateRangeChange}
                    />
                </div>
            </div>

            {/* Botão para Gerenciar Planos de Medicação */}
            {canAccessPlanos && (
                <div className="flex justify-end mb-2">
                    <button
                        onClick={() => navigate(`/pacientes/${pacienteId}/planos`)}
                        className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors duration-200 text-sm font-medium shadow-sm"
                        title="Gerenciar Planos de Medicação"
                    >
                        <Settings className="w-4 h-4" />
                        <span>Gerenciar Planos de Medicação</span>
                        <ExternalLink className="w-4 h-4" />
                    </button>
                </div>
            )}

            {isLoading && <LoadingIndicator message="Carregando histórico de medicações..." />}
            {error && <Alert type="error" message={error} />}

            {!isLoading && !error && (
                <>
                    {/* Navegação de Seções */}
                    <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => setActiveSection(section.id)}
                                className={`
                                    px-4 py-2 text-sm font-medium transition-colors duration-200
                                    ${activeSection === section.id
                                        ? 'border-b-2 border-emerald-500 text-emerald-600 font-bold'
                                        : 'text-gray-500 hover:text-emerald-600'
                                    }
                                `}
                            >
                                {section.label} ({section.count})
                            </button>
                        ))}
                    </div>

                    {/* Seção: Planos Ativos */}
                    {activeSection === 'planos-ativos' && (
                        <Card title={`Planos Médicos Ativos (${filteredData?.planos_ativos?.length || 0} ${filteredData?.planos_ativos?.length === 1 ? 'plano' : 'planos'})`}>
                            {filteredData?.planos_ativos && filteredData.planos_ativos.length > 0 ? (
                                <>
                                    {/* Desktop */}
                                    <div className="hidden md:block overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Medicamento</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dose</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequência</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Horário Base</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Período</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lote</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {filteredData.planos_ativos.map((plano) => (
                                                    <tr key={plano.id}>
                                                        <td className="px-4 py-2 text-sm font-medium text-gray-900">{plano.nome_medicamento}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-700">{plano.dose} {plano.unidade_medida}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-700">{formatFrequency(plano)}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-700">{formatTime(plano.horario_base)}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-700">
                                                            {formatDate(plano.data_inicio)} {plano.data_fim ? `até ${formatDate(plano.data_fim)}` : '(sem fim)'}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-700">
                                                            {plano.lote_numero ? (
                                                                <span>
                                                                    {plano.lote_numero}
                                                                    {plano.lote_data_validade && (
                                                                        <span className="text-xs text-gray-500 ml-1">
                                                                            (Válido até {formatDate(plano.lote_data_validade)})
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            ) : '-'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile */}
                                    <div className="md:hidden space-y-3">
                                        {filteredData.planos_ativos.map((plano) => (
                                            <div key={plano.id} className="border rounded-lg p-3 bg-green-50">
                                                <div className="font-medium text-gray-900 mb-2">{plano.nome_medicamento}</div>
                                                <div className="text-sm text-gray-700 space-y-1">
                                                    <div><span className="font-medium">Dose:</span> {plano.dose} {plano.unidade_medida}</div>
                                                    <div><span className="font-medium">Frequência:</span> {formatFrequency(plano)}</div>
                                                    <div><span className="font-medium">Horário:</span> {formatTime(plano.horario_base)}</div>
                                                    <div><span className="font-medium">Período:</span> {formatDate(plano.data_inicio)} {plano.data_fim ? `até ${formatDate(plano.data_fim)}` : '(sem fim)'}</div>
                                                    {plano.instrucoes_especificas && (
                                                        <div className="mt-2 text-xs text-gray-600 italic">{plano.instrucoes_especificas}</div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <p className="text-sm text-gray-500 p-4">Nenhum plano médico ativo encontrado.</p>
                            )}
                        </Card>
                    )}

                    {/* Seção: Planos Inativos (Histórico) */}
                    {activeSection === 'planos-inativos' && (
                        <Card title={`Histórico de Planos Médicos (${filteredData?.planos_inativos?.length || 0} ${filteredData?.planos_inativos?.length === 1 ? 'plano' : 'planos'})`}>
                            {filteredData?.planos_inativos && filteredData.planos_inativos.length > 0 ? (
                                <>
                                    {/* Desktop */}
                                    <div className="hidden md:block overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Medicamento</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dose</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequência</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Período</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {filteredData.planos_inativos.map((plano) => (
                                                    <tr key={plano.id}>
                                                        <td className="px-4 py-2 text-sm font-medium text-gray-900">{plano.nome_medicamento}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-700">{plano.dose} {plano.unidade_medida}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-700">{formatFrequency(plano)}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-700">
                                                            {formatDate(plano.data_inicio)} até {formatDate(plano.data_fim)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile */}
                                    <div className="md:hidden space-y-3">
                                        {filteredData.planos_inativos.map((plano) => (
                                            <div key={plano.id} className="border rounded-lg p-3 bg-gray-50">
                                                <div className="font-medium text-gray-900 mb-2">{plano.nome_medicamento}</div>
                                                <div className="text-sm text-gray-700 space-y-1">
                                                    <div><span className="font-medium">Dose:</span> {plano.dose} {plano.unidade_medida}</div>
                                                    <div><span className="font-medium">Frequência:</span> {formatFrequency(plano)}</div>
                                                    <div><span className="font-medium">Período:</span> {formatDate(plano.data_inicio)} até {formatDate(plano.data_fim)}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <p className="text-sm text-gray-500 p-4">Nenhum plano médico inativo encontrado.</p>
                            )}
                        </Card>
                    )}

                    {/* Seção: Tarefas Executadas */}
                    {activeSection === 'tarefas-executadas' && (
                        <Card title={`Tarefas de Medicação Executadas (${filteredData?.tarefas_executadas?.length || 0} ${filteredData?.tarefas_executadas?.length === 1 ? 'tarefa' : 'tarefas'})`}>
                            {filteredData?.tarefas_executadas && filteredData.tarefas_executadas.length > 0 ? (
                                <>
                                    {/* Desktop */}
                                    <div className="hidden md:block overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data/Hora</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Medicamento</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dose</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cuidador</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Observações</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {filteredData.tarefas_executadas.map((tarefa) => (
                                                    <tr key={tarefa.id}>
                                                        <td className="px-4 py-2 text-sm text-gray-700">{formatDate(tarefa.hora_conclusao || tarefa.hora_agendada)}</td>
                                                        <td className="px-4 py-2 text-sm font-medium text-gray-900">{tarefa.nome_medicamento || '-'}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-700">{tarefa.dose} {tarefa.unidade_medida}</td>
                                                        <td className="px-4 py-2 text-sm">
                                                            <span className={`
                                                                px-2 py-1 rounded-full text-xs font-medium
                                                                ${tarefa.status === 'Concluída' 
                                                                    ? 'bg-green-100 text-green-800' 
                                                                    : 'bg-yellow-100 text-yellow-800'
                                                                }
                                                            `}>
                                                                {tarefa.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-700">{tarefa.cuidador_responsavel_nome || tarefa.cuidador_responsavel || '-'}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-700">{tarefa.observacoes_conclusao || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile */}
                                    <div className="md:hidden space-y-3">
                                        {filteredData.tarefas_executadas.map((tarefa) => (
                                            <div key={tarefa.id} className="border rounded-lg p-3">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="font-medium text-gray-900">{tarefa.nome_medicamento || '-'}</div>
                                                    <span className={`
                                                        px-2 py-1 rounded-full text-xs font-medium
                                                        ${tarefa.status === 'Concluída' 
                                                            ? 'bg-green-100 text-green-800' 
                                                            : 'bg-yellow-100 text-yellow-800'
                                                        }
                                                    `}>
                                                        {tarefa.status}
                                                    </span>
                                                </div>
                                                <div className="text-sm text-gray-700 space-y-1">
                                                    <div><span className="font-medium">Data/Hora:</span> {formatDate(tarefa.hora_conclusao || tarefa.hora_agendada)}</div>
                                                    <div><span className="font-medium">Dose:</span> {tarefa.dose} {tarefa.unidade_medida}</div>
                                                    {(tarefa.cuidador_responsavel_nome || tarefa.cuidador_responsavel) && (
                                                        <div><span className="font-medium">Cuidador:</span> {tarefa.cuidador_responsavel_nome || tarefa.cuidador_responsavel}</div>
                                                    )}
                                                    {tarefa.observacoes_conclusao && (
                                                        <div className="mt-2 text-xs text-gray-600 italic">
                                                            <span className="font-medium">Observações:</span> {tarefa.observacoes_conclusao}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <p className="text-sm text-gray-500 p-4">Nenhuma tarefa de medicação executada encontrada.</p>
                            )}
                        </Card>
                    )}
                </>
            )}
        </div>
    );
};

export default PacienteMedicacaoTab;

